import { RouterLink as Link } from "../../router";
import { useState, type ReactNode } from "react";
import type { ActivityGroup, ActivityGroupMember } from "@excellent/core";
import { Card } from "../../card";
import { Count } from "../../count";
import { Icon } from "../../icons";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "../../tooltip";
import { formatDateTime, formatRelativeTime } from "../../format";
import { cn } from "../../utils";
import { ListCard, EmptyRow } from "../list-card";
import { DismissibleCard } from "../dismissible-card";

/**
 * The single, shared renderer for rolled-up activity (`ActivityGroup[]`, the
 * canonical shape from `@excellent/core`'s `groupActivities`). Every surface
 * that shows "recent activity" renders through one of these so the rollup looks
 * and behaves the same everywhere:
 *
 *   • `<ActivityGroupCard variant="rail|page">` — one group as its own <Card>.
 *     The app-shell activity rail (compact + dismiss + controlled expand) and
 *     the full /activity page (roomier, self-managed expand) both use this.
 *   • `<ActivityGroupList>` — a titled <ListCard> of grouped rows, for the
 *     dashboard grid, shell home, and workspace Directory (where a single
 *     "Recent activity" card fits the layout better than N standalone cards).
 *
 * Member labels: a member renders its `label` (e.g. a task title) when present,
 * else `#<entity_id>`. Expansion is offered only when a group has >1 member.
 */

/**
 * A clock glyph standing in for a noisy "x minutes ago" line — the exact
 * timestamp is one hover away. Keeps the rail card quiet at rest.
 */
function TimeChip({ at }: { at: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0 items-center text-muted-foreground/50">
          <Icon name="clock" size="xs" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="left">{formatDateTime(at)}</TooltipContent>
    </Tooltip>
  );
}

function memberText(m: ActivityGroupMember): string {
  return m.label && m.label.trim().length > 0 ? m.label : `#${m.entity_id}`;
}

/** The expandable member sample shared by every variant. */
function MemberSample({
  group,
  compact,
}: {
  group: ActivityGroup;
  compact: boolean;
}) {
  const base = compact
    ? "flex items-center justify-between gap-2 rounded px-1.5 py-1 type-subtext text-muted-foreground"
    : "flex items-center justify-between gap-2 py-1 type-subtext text-muted-foreground first:pt-0";
  return (
    <>
      {group.members.map((m) => {
        const content = (
          <>
            <span className="truncate">{memberText(m)}</span>
            {compact ? (
              <TimeChip at={m.occurred_at} />
            ) : (
              <span className="shrink-0 tabular-nums">
                {formatRelativeTime(m.occurred_at)}
              </span>
            )}
          </>
        );
        return m.href ? (
          <Link
            key={m.id}
            href={m.href}
            className={cn(base, "transition-colors hover:text-foreground")}
          >
            {content}
          </Link>
        ) : (
          <div key={m.id} className={base}>
            {content}
          </div>
        );
      })}
      {group.count > group.members.length ? (
        <div
          className={cn(
            "type-subtext text-muted-foreground/60",
            compact ? "px-1.5 py-1" : "py-1",
          )}
        >
          +{group.count - group.members.length} more
        </div>
      ) : null}
    </>
  );
}

// Compact (rail) card treatment: override the atom's py-6 down to py-3. The
// card's edge is the 1px box-shadow ring baked into the surface shadow
// (`--shadow-1` = `0 0 0 1px var(--shadow-color)`), normally a near-invisible
// 6% black; on hover we recolor that same edge by overriding the color variable
// it reads — darker in light, brighter in dark — so the existing border lights
// up instead of stacking a competing ring. Mirrors the rail SuggestionCard.
const RAIL_CARD =
  "py-3 transition-shadow hover:[--shadow-color:rgb(0_0_0_/_0.24)] dark:hover:[--dm-ring-base:rgb(255_255_255_/_0.18)]";

/**
 * One activity group as its own <Card>. `variant="rail"` is the compact,
 * dismissible, externally-controlled form used in the app-shell rail;
 * `variant="page"` is the roomier, self-expanding form used on /activity.
 */
export function ActivityGroupCard({
  group,
  variant = "page",
  expanded,
  onToggle,
  onDismiss,
}: {
  group: ActivityGroup;
  variant?: "rail" | "page";
  /** Controlled expand state (rail). When omitted, the card manages its own. */
  expanded?: boolean;
  onToggle?: () => void;
  /** When provided, the card is dismissible (rail "mark read" watermark). */
  onDismiss?: () => void;
}) {
  const compact = variant === "rail";
  const canExpand = group.count > 1;

  const [selfOpen, setSelfOpen] = useState(false);
  const isOpen = expanded ?? selfOpen;
  const toggle = onToggle ?? (() => setSelfOpen((v) => !v));

  // The label and its count pill hug each other on the LEFT, right beside the
  // thing being rolled up; time + caret are pushed to the right edge.
  const labelGroup = (
    <>
      <span className="min-w-0 truncate type-body text-foreground">
        {group.label}
      </span>
      {group.count > 1 ? <Count value={group.count} /> : null}
    </>
  );

  const timeNode = compact ? (
    <TimeChip at={group.latest} />
  ) : (
    <span className="shrink-0 type-subtext tabular-nums text-muted-foreground/60">
      {formatRelativeTime(group.latest)}
    </span>
  );

  const caretIcon = (
    <Icon
      name="chevron-right"
      size="xs"
      className={cn(
        "text-muted-foreground transition-transform",
        isOpen && "rotate-90",
      )}
    />
  );

  // --- Rail (compact) form. The label+count is a navigation link (a group
  // always goes SOMEWHERE: its own deep link, else the full /activity page).
  // No timestamp here — it wasn't pulling its weight and fought the controls.
  // The expand caret is pinned to the FAR-RIGHT edge in a fixed-width slot
  // that's ALWAYS reserved (an empty spacer when the group can't expand) so
  // rows stay column-aligned. Dismiss is a solid, obviously-clickable button
  // (filled background) revealed on hover of THIS card; it sits in normal flow
  // so it reserves its own slot and the caret never shifts. The trigger comes
  // in via DismissibleCard's render-child so the slide-out lives in one place.
  if (compact) {
    const navHref =
      group.href && group.href.length > 0 ? group.href : "/activity";

    const row = (dismiss?: () => void) => (
      <div className="flex items-center gap-1.5 px-4">
        <Link
          href={navHref}
          className="flex min-w-0 flex-1 items-center gap-2 transition-colors hover:text-foreground"
        >
          {labelGroup}
        </Link>
        {/* Dismiss — hidden at rest, revealed on card hover as a filled,
            obviously-tappable button. In normal flow (opacity, not absolute)
            so it reserves its slot and the caret stays put on the edge. */}
        {dismiss ? (
          <button
            type="button"
            aria-label={`Dismiss ${group.label}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dismiss();
            }}
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
              "bg-secondary text-muted-foreground opacity-0 transition-all",
              "group-hover/dismiss:opacity-100",
              "hover:bg-foreground/15 hover:text-foreground",
              "focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
            )}
          >
            <Icon name="x" size="sm" />
          </button>
        ) : null}
        {/* Caret slot — far right, always reserved (a spacer when not
            expandable) so rows stay column-aligned. */}
        {canExpand ? (
          <button
            type="button"
            onClick={toggle}
            aria-label={
              isOpen ? `Collapse ${group.label}` : `Expand ${group.label}`
            }
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          >
            {caretIcon}
          </button>
        ) : (
          <span className="h-5 w-5 shrink-0" aria-hidden />
        )}
      </div>
    );

    const body = (dismiss?: () => void) => (
      <div className="flex flex-col">
        {row(dismiss)}
        {isOpen && canExpand ? (
          <div className="space-y-px border-t border-border/40 px-4 pb-1.5 pt-1.5">
            <MemberSample group={group} compact />
          </div>
        ) : null}
      </div>
    );

    if (onDismiss) {
      return (
        <DismissibleCard
          className={RAIL_CARD}
          onDismiss={onDismiss}
          dismissLabel={`Dismiss ${group.label}`}
        >
          {({ dismiss }) => body(dismiss)}
        </DismissibleCard>
      );
    }
    return <Card className={RAIL_CARD}>{body()}</Card>;
  }

  // --- Page form: roomier, self-managed expand. The whole head toggles members
  // (multi-event), links to the entity (single-event with a route), or is static.
  const head = (
    <div className="flex items-center gap-2">
      {labelGroup}
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {timeNode}
        {canExpand ? caretIcon : null}
      </div>
    </div>
  );

  const body = (
    <div className="flex flex-col">
      {canExpand ? (
        <button type="button" onClick={toggle} className="text-left">
          {head}
        </button>
      ) : group.href ? (
        <Link href={group.href}>{head}</Link>
      ) : (
        <div>{head}</div>
      )}

      {isOpen && canExpand ? (
        <div className="mt-2 flex flex-col border-t border-border/40 pt-2">
          <MemberSample group={group} compact={false} />
        </div>
      ) : null}
    </div>
  );

  if (onDismiss) {
    return (
      <DismissibleCard
        onDismiss={onDismiss}
        dismissLabel={`Dismiss ${group.label}`}
      >
        {body}
      </DismissibleCard>
    );
  }

  return <Card className="p-4">{body}</Card>;
}

/**
 * A titled <ListCard> of rolled-up activity, for the dashboard grid / shell
 * home / Directory. Each group is one expandable row (list-row pattern), so a
 * busy workspace stays a handful of countable rows instead of a firehose.
 */
export function ActivityGroupList({
  groups,
  title = "Recent activity",
  emptyBody = "No activity yet.",
  action,
  onDismiss,
}: {
  groups: ActivityGroup[];
  title?: ReactNode;
  emptyBody?: ReactNode;
  action?: ReactNode;
  /**
   * When provided, each row gets the same persistent dismiss affordance the
   * app-shell activity rail uses — Home wires this to `activity.dismiss`. Omit
   * on read-only surfaces (dashboards, Directory) and rows stay non-dismissible.
   */
  onDismiss?: (groupKey: string) => void;
}) {
  // Defensive de-dup: the row key is `g.key` (also the dismissal id), so a
  // duplicate would crash React with "two children with the same key". Callers
  // that MERGE grouped rollups from several sources should combine via
  // `mergeActivityGroups` (which SUMS counts); this keeps the first of any
  // stragglers so the list can never crash even if a caller forgets.
  const seen = new Set<string>();
  const rows = groups.filter((g) =>
    seen.has(g.key) ? false : (seen.add(g.key), true),
  );
  return (
    <ListCard title={title} count={rows.length} action={action}>
      {rows.length === 0 ? (
        <EmptyRow>{emptyBody}</EmptyRow>
      ) : (
        // Rows follow the FeedList visual conventions (px-6 gutter, hairline
        // dividers, alternating backgrounds) so this card reads like every
        // other ListCard — it can't BE a FeedList because rows expand.
        <div className="flex flex-col">
          {rows.map((g) => (
            <ActivityGroupRow
              key={g.key}
              group={g}
              onDismiss={onDismiss ? () => onDismiss(g.key) : undefined}
            />
          ))}
        </div>
      )}
    </ListCard>
  );
}

/** One group as an expandable list row (no per-group card chrome). */
function ActivityGroupRow({
  group,
  onDismiss,
}: {
  group: ActivityGroup;
  onDismiss?: () => void;
}) {
  const canExpand = group.count > 1;
  const [open, setOpen] = useState(false);

  const head = (
    <div className="flex items-center gap-2">
      {canExpand ? (
        <Icon
          name="chevron-right"
          size="xs"
          className={cn(
            "shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90",
          )}
        />
      ) : (
        <span className="w-3 shrink-0" aria-hidden />
      )}
      <span className="min-w-0 flex-1 truncate type-body text-foreground">
        {group.label}
      </span>
      {group.count > 1 ? (
        <span className="shrink-0 type-subtext tabular-nums text-muted-foreground">
          {group.count}
        </span>
      ) : null}
      <span className="shrink-0 type-subtext tabular-nums text-muted-foreground/60">
        {formatRelativeTime(group.latest)}
      </span>
    </div>
  );

  return (
    <div className="group/act relative border-b border-border px-6 py-3 type-body last:border-b-0 even:bg-foreground/[0.025] transition-colors">
      {canExpand ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn("w-full text-left", onDismiss && "pr-6")}
        >
          {head}
        </button>
      ) : group.href ? (
        <Link href={group.href} className={cn("block", onDismiss && "pr-6")}>
          {head}
        </Link>
      ) : (
        <div className={onDismiss ? "pr-6" : undefined}>{head}</div>
      )}
      {onDismiss ? (
        <button
          type="button"
          aria-label={`Dismiss ${group.label}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDismiss();
          }}
          className={cn(
            "absolute right-4 top-3 rounded p-0.5 text-muted-foreground/50",
            "opacity-60 transition-all group-hover/act:opacity-100",
            "hover:bg-secondary hover:text-foreground",
            "focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
          )}
        >
          <Icon name="x" size="xs" />
        </button>
      ) : null}
      {open && canExpand ? (
        <div className="mt-1.5 flex flex-col border-t border-border/40 pl-5 pt-1.5">
          <MemberSample group={group} compact={false} />
        </div>
      ) : null}
    </div>
  );
}
